import { AbstractControl, ValidatorFn } from '@angular/forms';

/**
 * True only when the value is a real, non-empty signature image data URL
 * (e.g. "data:image/png;base64,<payload>").
 *
 * Guards every signing flow against DIGLIEF-9118: a blank signature pad,
 * an empty pulled blob or a non-image response must never count as "signed".
 * Deliberately strict ("data:image"): the signature endpoints respond with
 * image/png, anything else has to fail closed.
 * @param {unknown} value - The candidate signature value (usually a form control value).
 * @returns {boolean} - True if the value is a non-empty signature image data URL.
 */
export function isValidSignatureDataUrl(value: unknown): value is string {
    return (
        typeof value === 'string' &&
        value.startsWith('data:image') &&
        (value.split(',')[1]?.length ?? 0) > 0
    );
}

/**
 * This function returns a ValidatorFn that checks if the control holds a real,
 * non-empty signature image data URL (see isValidSignatureDataUrl).
 * Used on signature form controls so that "signatureForm.invalid" button
 * gating cannot be satisfied by a blank or non-image value.
 * @returns {{[key: string]: any} | null} - An object containing a 'signatureRequired' key if the validation fails, or null if it passes.
 */
export function signatureRequiredValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
        return isValidSignatureDataUrl(control.value)
            ? null
            : { signatureRequired: true };
    };
}
